import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from "react";
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS, Step, Styles } from "react-joyride";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/lib/firestore";

interface WalkthroughContextType {
  startWalkthrough: () => void;
  startConsultantWalkthrough: () => void;
  isRunning: boolean;
  hasCompleted: boolean;
  audioEnabled: boolean;
  toggleAudio: () => void;
}

interface WalkthroughStep extends Step {
  route?: string;
}

const WalkthroughContext = createContext<WalkthroughContextType | undefined>(undefined);

const WALKTHROUGH_STORAGE_KEY = "applywise_walkthrough_completed";
const AUDIO_PREFERENCE_KEY = "applywise_walkthrough_audio";

// Custom styles for the tour
const joyrideStyles: Partial<Styles> = {
  options: {
    zIndex: 10000,
    primaryColor: "hsl(var(--primary))",
    backgroundColor: "hsl(var(--card))",
    textColor: "hsl(var(--foreground))",
    arrowColor: "hsl(var(--card))",
  },
  spotlight: {
    backgroundColor: "transparent",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  tooltip: {
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.3)",
  },
  tooltipContainer: {
    textAlign: "left" as const,
  },
  tooltipTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "8px",
  },
  tooltipContent: {
    fontSize: "14px",
    lineHeight: 1.6,
  },
  buttonNext: {
    backgroundColor: "hsl(var(--primary))",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: 500,
  },
  buttonBack: {
    marginRight: "8px",
    color: "hsl(var(--muted-foreground))",
  },
  buttonSkip: {
    color: "hsl(var(--muted-foreground))",
  },
};

// Define steps for the walkthrough with routes
const getWalkthroughSteps = (): WalkthroughStep[] => [
  {
    target: "body",
    content:
      " This quick walkthrough will guide you through the key features of the platform and help you manage your study-abroad applications with confidence.",
    placement: "center",
    title: "👋 Welcome to ApplyWisely",
    disableBeacon: true,
    route: "/dashboard",
  },
  {
    target: '[data-walkthrough="dashboard"]',
    content:
      "A complete overview of your application journey. Track progress, view important updates, and stay informed at a glance.",
    placement: "right",
    title: "📊 Your Dashboard",
    disableBeacon: true,
    route: "/dashboard",
  },
  {
    target: '[data-walkthrough="universities"]',
    content:
      "Manage all your university applications here. Add universities, track deadlines, and update application statuses as you move forward.",
    placement: "right",
    title: "🎓 University Applications",
    disableBeacon: true,
    route: "/universities",
  },
  {
    target: '[data-walkthrough="documents"]',
    content:
      "Store and organize all your essential documents in one secure place — SOPs, transcripts, resumes, recommendation letters, and more.",
    placement: "right",
    title: "📁 Document Management",
    disableBeacon: true,
    route: "/documents",
  },
  {
    target: '[data-walkthrough="inbox"]',
    content:
      "Keep track of important university communications in one inbox. Messages are organized so you never miss critical updates.",
    placement: "right",
    title: "📧 Application Inbox",
    disableBeacon: true,
    route: "/inbox",
  },
  {
    target: '[data-walkthrough="deadlines"]',
    content:
      "View all your important deadlines in one place. Stay on schedule with a clear timeline of upcoming milestones.",
    placement: "right",
    title: "📅 Deadlines & Timeline",
    disableBeacon: true,
    route: "/deadlines",
  },
  {
    target: '[data-walkthrough="activity-log"]',
    content:
      "Review your activity history across the platform. Every update and action is tracked so you always know what has changed.",
    placement: "right",
    title: "📋 Activity History",
    disableBeacon: true,
    route: "/logs",
  },
  {
    target: '[data-walkthrough="settings"]',
    content: "Personalize your experience by managing account settings, notifications, and preferences from here.",
    placement: "right",
    title: "⚙️ Account Settings",
    disableBeacon: true,
    route: "/settings",
  },
  {
    target: "body",
    content:
      "You’re all set! Start by adding your first university or exploring the dashboard. You can revisit this walkthrough anytime using the “Watch Walkthrough” button on your dashboard.",
    placement: "center",
    title: "🎉 Ready to Get Started",
    disableBeacon: true,
    route: "/dashboard",
  },
];

const stopSpeaking = () => {
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
  }
};

// ── Consultant onboarding steps ───────────────────────────────────────────────
// Targets use data-walkthrough attributes set on ConsultantDashboard / StudentDetail.
const CONSULTANT_WALKTHROUGH_STEPS: WalkthroughStep[] = [
  {
    target: "body",
    content:
      "This quick tour covers the key areas of your consultant workspace. You can skip it at any time.",
    placement: "center",
    title: "👋 Welcome, Consultant!",
    disableBeacon: true,
  },
  {
    target: '[data-walkthrough="consultant-dashboard"]',
    content:
      "This is your student dashboard. Here you can see all your students, their application status, and upcoming deadlines at a glance.",
    placement: "top",
    title: "📊 Your Dashboard",
    disableBeacon: true,
  },
  {
    target: '[data-walkthrough="add-student-btn"]',
    content:
      "Click here to add a new student. You'll fill in their profile, target countries, and intake details.",
    placement: "bottom",
    title: "➕ Add Your First Student",
    disableBeacon: true,
  },
  {
    target: '[data-walkthrough="dashboard-actions"]',
    content:
      "Use these controls to import students via CSV or quickly add individual students to build up your client list.",
    placement: "bottom",
    title: "⚡ Quick Actions",
    disableBeacon: true,
  },
  {
    target: "body",
    content:
      "You're all set! Start by adding your first student. The tour can be relaunched from the settings menu anytime.",
    placement: "center",
    title: "🎉 Ready to Go!",
    disableBeacon: true,
  },
];

const getAudioSrcForStep = (stepIdx: number) => `/audio/Walkthrough_V${stepIdx + 1}.mp3`;

export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const [isRunning, setIsRunning] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [walkthroughType, setWalkthroughType] = useState<'student' | 'consultant'>('student');
  const [audioEnabled, setAudioEnabled] = useState(() => {
    const saved = localStorage.getItem(AUDIO_PREFERENCE_KEY);
    return saved !== "false"; // Default to true
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const steps = walkthroughType === 'consultant' ? CONSULTANT_WALKTHROUGH_STEPS : getWalkthroughSteps();
  const isNavigatingRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Load voices on mount
  useEffect(() => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }, []);

  // Toggle audio preference
  const toggleAudio = useCallback(() => {
    setAudioEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem(AUDIO_PREFERENCE_KEY, String(newValue));
      if (!newValue) {
        stopSpeaking();
        stopWalkthroughAudio();
      }
      return newValue;
    });
  }, []);

  // Check if walkthrough was completed
  // Use localStorage as fast check first, then sync with Firestore for logged-in users
  useEffect(() => {
    const checkWalkthroughStatus = async () => {
      // Always check localStorage first (works for guests and as cache for signed-in users)
      const localCompleted = localStorage.getItem(WALKTHROUGH_STORAGE_KEY) === "true";

      if (!user) {
        // Guest: only use localStorage
        setHasCompleted(localCompleted);
        setIsInitialized(true);
        return;
      }

      // Signed-in user: use localStorage as fast default, then verify with Firestore
      if (localCompleted) {
        setHasCompleted(true);
        setIsInitialized(true);
        return;
      }

      // Not completed locally — check Firestore
      try {
        const docRef = doc(db, "users", user.uid, "preferences", "walkthrough");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data()?.completed) {
          setHasCompleted(true);
          // Sync to localStorage so we don't hit Firestore again
          localStorage.setItem(WALKTHROUGH_STORAGE_KEY, "true");
        } else {
          setHasCompleted(false);
        }
      } catch (error) {
        // Permission error or network issue — fall back to not completed
        // This prevents the console error for edge cases
        console.warn("Could not check walkthrough status from Firestore, using local state");
        setHasCompleted(false);
      }
      setIsInitialized(true);
    };

    checkWalkthroughStatus();
  }, [user]);

  // Auto-start walkthrough for first-time users on dashboard:
  // - Guests: always auto-show on first visit
  // - Logged-in users: auto-show on first login, then never again
  useEffect(() => {
    if (isInitialized && !hasCompleted && location.pathname === "/dashboard" && !isRunning) {
      const timer = setTimeout(() => {
        setIsRunning(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isInitialized, hasCompleted, location.pathname, isRunning]);

  const playWalkthroughAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  }, []);

  const stopWalkthroughAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  // When step changes, play audio if enabled
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.src = getAudioSrcForStep(stepIndex);
    }
    if (isRunning && audioEnabled) {
      playWalkthroughAudio();
    } else {
      stopWalkthroughAudio();
    }
  }, [stepIndex, isRunning, audioEnabled, playWalkthroughAudio, stopWalkthroughAudio]);

  // Stop speaking when walkthrough stops
  useEffect(() => {
    if (!isRunning) {
      stopSpeaking();
      stopWalkthroughAudio();
    }
  }, [isRunning]);

  // Save completion status — always save to localStorage + Firestore for logged-in users
  const saveCompletionStatus = useCallback(async () => {
    // Always set localStorage (fast cache for both guests and logged-in users)
    localStorage.setItem(WALKTHROUGH_STORAGE_KEY, "true");
    setHasCompleted(true);

    if (user) {
      try {
        const docRef = doc(db, "users", user.uid, "preferences", "walkthrough");
        await setDoc(docRef, { completed: true, completedAt: new Date().toISOString() });
      } catch (error) {
        console.warn("Could not save walkthrough status to Firestore:", error);
      }
    }
  }, [user]);

  const startWalkthrough = useCallback(() => {
    stopSpeaking();
    stopWalkthroughAudio();
    if (location.pathname !== "/dashboard") {
      navigate("/dashboard");
      setTimeout(() => {
        setWalkthroughType('student');
        setStepIndex(0);
        setIsRunning(true);
      }, 500);
    } else {
      setWalkthroughType('student');
      setStepIndex(0);
      setIsRunning(true);
    }
  }, [location.pathname, navigate]);

  const startConsultantWalkthrough = useCallback(() => {
    stopSpeaking();
    stopWalkthroughAudio();
    setWalkthroughType('consultant');
    setStepIndex(0);
    setIsRunning(true);
  }, []);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action, index, type } = data;
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

      if (finishedStatuses.includes(status)) {
        stopSpeaking();
        stopWalkthroughAudio();
        setIsRunning(false);
        saveCompletionStatus();
        // Navigate back to dashboard when finished
        if (location.pathname !== "/dashboard") {
          navigate("/dashboard");
        }
      } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        stopSpeaking();
        stopWalkthroughAudio();
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        const nextStep = steps[nextIndex];

        if (nextStep && nextStep.route && nextStep.route !== location.pathname) {
          isNavigatingRef.current = true;
          navigate(nextStep.route);
          // Wait for navigation to complete before updating step
          setTimeout(() => {
            isNavigatingRef.current = false;
            setStepIndex(nextIndex);
          }, 400);
        } else {
          setStepIndex(nextIndex);
        }
      }
    },
    [saveCompletionStatus, navigate, location.pathname, steps],
  );

  return (
    <WalkthroughContext.Provider value={{ startWalkthrough, startConsultantWalkthrough, isRunning, hasCompleted, audioEnabled, toggleAudio }}>
      {children}
      {/* Per-step walkthrough audio with error handling */}
      <audio
        ref={audioRef}
        preload="auto"
        onError={() => {
          console.warn('Walkthrough audio file not found or unsupported format.');
        }}
      >
        Your browser does not support the audio element or the audio file is missing.
      </audio>
      <Joyride
        steps={steps}
        run={isRunning}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        spotlightClicks
        disableOverlayClose
        callback={handleJoyrideCallback}
        styles={joyrideStyles}
        locale={{
          back: "Back",
          close: "Close",
          last: "Finish",
          next: "Next",
          open: "Open",
          skip: "Skip Tour",
        }}
        floaterProps={{
          disableAnimation: false,
        }}
      />
    </WalkthroughContext.Provider>
  );
}

export function useWalkthrough() {
  const context = useContext(WalkthroughContext);
  if (context === undefined) {
    throw new Error("useWalkthrough must be used within a WalkthroughProvider");
  }
  return context;
}
